import { postJSON } from "../api.js";
import { cls, esc } from "../ui/format.js";
import { uiCard, uiEmpty, uiGet, uiMoney, uiPill } from "../ui/kit.js";
import { toast } from "../ui/modals.js";
import { openLaunchProductModal } from "./launch.js";

/**
 * The Product Forge (MASTER_PROMPT 7A), 2D. Shared by the launch studio and
 * the Products screen:
 *   - fgDecorate: the live Forge read of a build inside the studio — combos
 *     found (with a reveal the first time), clash warnings glowing on the
 *     option cards before you pick them, the team's hunches, how each
 *     customer group reacts, and the rival most likely to answer.
 *   - fgPrototypeCard: the industry's prototype test; reveals the build's
 *     hidden quality as a range that narrows with each re-test.
 *   - fgOpenVariants: spin-off variants after a launch (keep one, and it
 *     becomes a sibling product in the family tree).
 *   - fgOpenEvolve: a new generation, changing up to two dimensions.
 *   - fgQuickBuild: the product executive's one-tap build (3-tap rule:
 *     Quick Build -> Launch -> keep a variant).
 *   - fgRenderPortfolio: Product Family Tree and the Forge Codex.
 * Data: /api/forge/* (backend/forge.py). Actions: /api/actions/execute.
 */

const fgSeen = new Set();   // combos already revealed in this session

async function fgExec(type, params) {
  const r = await postJSON("/actions/execute", { action: { type, params } });
  return r.message;
}

function fgChip(a) {
  const cls = a.kind === "combo" ? "fg-chip combo" : "fg-chip clash";
  const fresh = a.kind === "combo" && a.new && !fgSeen.has(a.name);
  return `<span class="${cls}${fresh ? " fg-reveal" : ""}">${a.kind === "combo" ? "✦" : "⚠"} ${esc(a.name)}${a.new && a.kind === "combo" ? " <em>new</em>" : ""}</span>`;
}

function fgEffectLine(e) {
  const parts = [];
  if (e.tier) parts.push(`${e.tier > 0 ? "+" : ""}${Math.round(e.tier * 100)} sophistication`);
  if (e.ramp && Math.abs(e.ramp - 1) > 0.001) parts.push(`revenue per $ ×${e.ramp.toFixed(2)}`);
  if (e.growth) parts.push(`${e.growth > 0 ? "+" : ""}${(e.growth * 100).toFixed(1)} pts growth/mo`);
  if (e.brand) parts.push(`brand ${e.brand > 0 ? "+" : ""}${e.brand}`);
  return parts.join(" · ");
}

function fgPanel(f) {
  const reactions = f.customers.map((c) => `
    <div class="fg-cust ${c.reaction.replace(" ", "-")}">
      <span>${esc(c.group)}</span><div><i style="width:${c.share_pct}%"></i></div><b>${esc(c.reaction)}</b></div>`).join("");
  return `
    <div class="fg-panel">
      <div class="fg-head"><span class="sx-eyebrow">The ${esc(f.studio)}</span><span class="fg-codex-count">Codex ${f.codex.found}/${f.codex.total}</span></div>
      ${f.active.length ? `<div class="fg-chips">${f.active.map(fgChip).join("")}</div><div class="fg-effect">${esc(fgEffectLine(f.effect))}</div>`
        : `<div class="fg-quiet">No combos in this build yet.</div>`}
      ${f.hints.map((h) => `<div class="fg-hint"><b>Team hunch · ${esc(h.dimension)}:</b> ${esc(h.hint)}</div>`).join("")}
      <div class="fg-sub">Who it appeals to</div>
      <div class="fg-custs">${reactions}</div>
      ${f.nemesis ? `<div class="fg-rival"><span class="fg-rival-face">${esc(f.nemesis.name.slice(0, 1))}</span><div><b>Rival watch</b>${esc(f.nemesis.line)}.</div></div>` : ""}
    </div>`;
}

/** Decorate the studio's spec step with the live Forge read. */
async function fgDecorate(root, studio) {
  const host = root.querySelector("#lx-forge");
  if (!host) return null;
  let f;
  try { f = await postJSON("/forge/build", { selections: studio.selections }); } catch (e) { host.innerHTML = ""; return null; }
  if (!root.contains(host)) return null;
  host.innerHTML = fgPanel(f);
  f.active.forEach((a) => { if (a.kind === "combo") fgSeen.add(a.name); });
  // Clash warnings glow on the option that would complete the clash.
  const byLabel = {};
  f.clash_warnings.forEach((w) => { byLabel[w.option] = w; });
  root.querySelectorAll(".lx-opt").forEach((card) => {
    const label = card.querySelector(".lx-opt-label")?.textContent;
    const w = byLabel[label];
    card.classList.toggle("fg-clash", !!w);
    card.querySelector(".fg-clash-note")?.remove();
    if (w) card.insertAdjacentHTML("beforeend", `<div class="fg-clash-note">⚠ Clash: ${esc(w.clash)}</div>`);
  });
  return f;
}

function fgRangeBar(t) {
  const pos = (v) => ((v + 8) / 16) * 100;
  return `<div class="fg-range"><div class="fg-range-track">
      <span class="fg-zero"></span>
      <i style="left:${pos(t.low)}%; width:${pos(t.high) - pos(t.low)}%" class="${t.tested ? "" : "unknown"}"></i></div>
    <div class="fg-range-scale"><span>−8 worse</span><span>average</span><span>+8 better</span></div></div>`;
}

/** The prototype test card for a build (used in the studio's review step). */
async function fgPrototypeCard(host, selections) {
  let f;
  try { f = await postJSON("/forge/build", { selections }); } catch (e) { host.innerHTML = ""; return; }
  const t = f.test;
  host.innerHTML = `
    <div class="fg-test">
      <div class="fg-test-head"><div><span class="sx-eyebrow">Prototype test</span><h4>${esc(t.name)}</h4><p>${esc(t.what)}</p></div>
        <div class="fg-test-btns"><button type="button" id="fg-run-test">${t.tested ? "Re-test" : "Run"} · ${uiMoney(t.cost, { compact: true })}</button>
          <button type="button" id="fg-share" title="A short code anyone can load to build this exact configuration">🔗 Share blueprint</button></div></div>
      <div class="fg-bp hidden" id="fg-bp"></div>
      <div class="fg-sub">Customer satisfaction versus an average build</div>
      ${fgRangeBar(t)}
      <div class="fg-test-read">${t.tested
        ? `Between <b>${t.low >= 0 ? "+" : ""}${t.low.toFixed(1)}</b> and <b>${t.high >= 0 ? "+" : ""}${t.high.toFixed(1)}</b> points after ${t.tests} test${t.tests > 1 ? "s" : ""}.
           <br>${Object.entries(t.metrics || {}).map(([k, v]) => `<span class="fg-metric">${esc(k)} score <b>${v[0]}–${v[1]}</b>/100</span>`).join("")} <span class="fg-metric-note">(scores: higher is better)</span>`
        : "Untested: anywhere from −8 to +8. Every build has a hidden quality customers will feel; a test reveals it before you commit. Better operations give a sharper first read; each re-test (one a month) halves the range."}</div>
    </div>`;
  host.querySelector("#fg-share").addEventListener("click", async () => {
    const out = host.querySelector("#fg-bp");
    try {
      const bp = await postJSON("/forge/blueprint", { selections });
      out.innerHTML = `Blueprint <b class="fg-bp-code">${esc(bp.code)}</b> · paste it into Launch a Product on any save in your industry.`;
      out.classList.remove("hidden");
      if (navigator.clipboard) navigator.clipboard.writeText(bp.code).then(() => toast(`Copied ${bp.code}`), () => {});
    } catch (err) { toast(err.message, true); }
  });
  host.querySelector("#fg-run-test").addEventListener("click", async (e) => {
    e.target.disabled = true;
    try { toast(await fgExec("forge_prototype", { selections })); } catch (err) { toast(err.message, true); }
    await fgPrototypeCard(host, selections);
  });
}

function fgVariantCard(v, i) {
  return `
    <div class="fg-var">
      <div class="fg-var-top"><span class="sx-eyebrow">${esc(v.axis)}</span>${uiPill(`${v.market_pct}% of the market`)}</div>
      <h4>${esc(v.tag)}</h4>
      <p>${esc(v.blurb)}</p>
      <ul class="fg-changes">${v.changes.map((c) => `<li><span>${esc(c.dimension)}</span>${esc(c.from)} → <b>${esc(c.to)}</b></li>`).join("")}</ul>
      ${v.combos.length || v.clashes.length ? `<div class="fg-chips">${v.combos.map((n) => fgChip({ kind: "combo", name: n })).join("")}${v.clashes.map((n) => fgChip({ kind: "clash", name: n })).join("")}</div>` : ""}
      <div class="fg-var-nums">
        <div><span>Costs</span><b>${uiMoney(v.cash_cost, { compact: true })}</b><em>${uiMoney(v.full_budget, { compact: true })} platform, 40% reused</em></div>
        <div><span>Starts at</span><b>${uiMoney(v.start_revenue, { compact: true })}/mo</b><em>${v.growth_pct}%/mo growth</em></div>
        <div><span>Family overlap</span><b class="neg">${v.family_hit_pct}%/mo</b><em>for 6 months</em></div>
      </div>
      <button type="button" class="primary" data-keep="${i}">Keep this variant</button>
    </div>`;
}

/** Spin-off variants for a product. */
async function fgOpenVariants(productId, { afterLaunch = false } = {}) {
  const p = await uiGet(`/forge/variants/${productId}`);
  const backdrop = document.createElement("div");
  backdrop.className = "modal-backdrop";
  const groups = {};
  p.variants.forEach((v, i) => { (groups[v.axis] = groups[v.axis] || []).push([v, i]); });
  backdrop.innerHTML = `
    <div class="modal modal-page fg-modal">
      <div class="modal-page-header">
        <h3>${afterLaunch ? "Launched. Grow the family?" : "Spin off a variant"}</h3>
        <p>Each choice you made suggests spin-offs. A variant reuses this product's platform R&D (40% cheaper), sells into its own slice of the market, and pulls a little demand from its family. ${p.exec ? `${esc(p.exec.name)} (skill ${p.exec.skill}) placed these.` : "No product executive: these proposals are 10% weaker."}</p>
        <div class="fg-room">Family room: <b>${p.room}</b> of ${p.cap} variant slots left${p.room === 0 ? " · reach more goals to grow it" : ""}</div>
      </div>
      <div class="modal-page-body">${p.variants.length ? Object.entries(groups).map(([axis, list]) => `
        <section class="fg-axis"><h4>${esc(axis)}</h4><div class="fg-vars">${list.map(([v, i]) => fgVariantCard(v, i)).join("")}</div></section>`).join("")
        : uiEmpty("No variants left", "Every spin-off this product suggests already exists.")}</div>
      <div class="modal-page-footer"><div></div><div class="modal-actions"><button type="button" id="fg-close">${afterLaunch ? "Not now" : "Close"}</button></div></div>
    </div>`;
  document.body.appendChild(backdrop);
  const close = () => backdrop.remove();
  backdrop.querySelector("#fg-close").addEventListener("click", close);
  backdrop.querySelectorAll("[data-keep]").forEach((b) => {
    if (p.room <= 0) b.disabled = true;
    b.addEventListener("click", async () => {
      const v = p.variants[Number(b.dataset.keep)];
      b.disabled = true;
      try { toast(await fgExec("forge_spin_variant", { product_id: productId, axis: v.axis, tag: v.tag })); close(); await window.refreshAll(); }
      catch (e) { b.disabled = false; toast(e.message, true); }
    });
  });
}

/** A new generation: change up to two dimensions. */
async function fgOpenEvolve(node) {
  const schema = await uiGet("/products/schema");
  const changes = {};
  const backdrop = document.createElement("div");
  backdrop.className = "modal-backdrop";
  const render = () => {
    const n = Object.keys(changes).length;
    backdrop.innerHTML = `
      <div class="modal modal-page fg-modal">
        <div class="modal-page-header"><h3>Evolve ${esc(node.name)} into v${(node.generation || 1) + 1}</h3>
          <p>Keep what works and change up to two things. The new generation keeps half the R&D, and the old one keeps selling while customers move over.</p>
          <div class="fg-room">${n}/2 changes</div></div>
        <div class="modal-page-body">${schema.categories.map((c) => {
          const cur = node.config[c.category];
          const locked = n >= 2 && !(c.category in changes);
          return `<section class="fg-evo-dim ${locked ? "locked" : ""}"><h4>${esc(c.category)}</h4><div class="fg-evo-opts">${c.options.map((o) => {
            const on = (changes[c.category] || cur) === o.label;
            return `<button type="button" class="fg-evo-opt ${on ? "on" : ""} ${o.label === cur ? "current" : ""}" data-cat="${esc(c.category)}" data-label="${esc(o.label)}" ${locked && o.label !== cur ? "disabled" : ""}>
              <span class="sx-eyebrow">${esc(o.tier_label)}</span>${esc(o.label)}${o.label === cur ? "<em>today</em>" : ""}</button>`;
          }).join("")}</div></section>`;
        }).join("")}</div>
        <div class="modal-page-footer"><div></div><div class="modal-actions"><button type="button" id="fg-close">Cancel</button>
          <button type="button" class="primary" id="fg-evolve" ${n ? "" : "disabled"}>Build v${(node.generation || 1) + 1}</button></div></div>
      </div>`;
    backdrop.querySelector("#fg-close").addEventListener("click", () => backdrop.remove());
    backdrop.querySelectorAll(".fg-evo-opt").forEach((b) => b.addEventListener("click", () => {
      const cat = b.dataset.cat, label = b.dataset.label;
      if (label === node.config[cat]) delete changes[cat]; else changes[cat] = label;
      render();
    }));
    backdrop.querySelector("#fg-evolve").addEventListener("click", async (e) => {
      e.target.disabled = true;
      try { toast(await fgExec("forge_evolve", { product_id: node.id, changes })); backdrop.remove(); await window.refreshAll(); }
      catch (err) { e.target.disabled = false; toast(err.message, true); }
    });
  };
  document.body.appendChild(backdrop);
  render();
}

/** Quick Build: one tap for the exec's proposal, one to launch. */
async function fgQuickBuild() {
  const q = await uiGet("/forge/quick-build");
  const backdrop = document.createElement("div");
  backdrop.className = "modal-backdrop";
  backdrop.innerHTML = `
    <div class="modal fg-quick">
      <span class="sx-eyebrow">Quick Build</span>
      <h3>${esc(q.name)}</h3>
      <p class="fg-why">${esc(q.why)}</p>
      <div class="fg-quick-config">${Object.entries(q.labels).map(([d, l]) => `<div><span>${esc(d)}</span><b>${esc(l)}</b></div>`).join("")}</div>
      <div class="fg-quick-budget"><span>Development budget</span><b>${uiMoney(q.investment)}</b></div>
      <div class="modal-actions"><button type="button" id="fg-q-deep">Build it myself</button><button type="button" class="primary" id="fg-q-go">Launch it</button></div>
    </div>`;
  document.body.appendChild(backdrop);
  backdrop.querySelector("#fg-q-deep").addEventListener("click", () => { backdrop.remove(); openLaunchProductModal(); });
  backdrop.querySelector("#fg-q-go").addEventListener("click", async (e) => {
    e.target.disabled = true;
    try {
      const r = await postJSON("/products/launch", { name: q.name, investment: q.investment, selections: q.selections,
                                                   marketing_selections: {}, marketing_budget: 0 });
      backdrop.remove();
      toast(r.message);
      await window.refreshAll();
      if (r.product_id) await fgOpenVariants(r.product_id, { afterLaunch: true });
    } catch (err) { e.target.disabled = false; toast(err.message, true); }
  });
}

function fgNode(m, cls = "") {
  return `<div class="fg-node ${cls} ${m.cannibalized_pct ? "eaten" : ""}" data-node="${m.id}">
    <div class="fg-node-top">${m.tag ? uiPill(m.tag, m.axis === "Generation" ? "info" : "") : uiPill("Original", "good")}<span>M${m.launch_month}</span></div>
    <b>${esc(m.name)}</b>
    <div class="fg-node-nums"><span>${uiMoney(m.revenue, { compact: true })}/mo</span>${m.cannibalized_pct ? `<span class="neg">${m.cannibalized_pct}%/mo overlap</span>` : ""}</div>
    <div class="fg-node-acts"><button type="button" data-spin="${m.id}">Spin off</button><button type="button" data-evolve="${m.id}">Evolve</button></div>
  </div>`;
}

/** Product Family Tree and Forge Codex on the Products screen. */
async function fgRenderPortfolio(host) {
  if (!host) return;
  const [tree, codex] = await Promise.all([uiGet("/forge/family"), uiGet("/forge/codex")]);
  const nodes = {};
  tree.families.forEach((f) => f.members.forEach((m) => { nodes[m.id] = m; }));
  const familyHtml = (f) => {
    const kids = (pid) => f.members.filter((m) => m.parent_id === pid);
    const branch = (m) => {
      const ch = kids(m.id);
      return `<li>${fgNode(m)}${ch.length ? `<ul>${ch.map(branch).join("")}</ul>` : ""}</li>`;
    };
    return `<div class="fg-family ${f.tangled ? "tangled" : ""}">
      ${f.tangled ? `<div class="fg-tangle">Tangled: ${f.members.filter((m) => m.cannibalized_pct).length} products are eating each other's demand right now.</div>` : ""}
      <ul class="fg-tree">${branch(f.root)}</ul></div>`;
  };
  host.innerHTML = `
    ${uiCard("Product family tree", tree.families.length ? tree.families.map(familyHtml).join("")
      : uiEmpty("No products yet", "Launch one and its variants will branch from it here."),
      { sub: `Each product can grow up to ${tree.cap} variants (more as you reach goals). Variants reuse R&D; too many overlapping ones eat each other.` })}
    ${uiCard(`Forge Codex · ${codex.found} of ${codex.total} found`, `<div class="fg-codex">${codex.entries.map((e) => `
      <div class="fg-cx ${e.kind} ${e.found ? "found" : ""} ${e.name ? "" : "hidden"}">
        <div class="fg-cx-top"><span>${e.kind === "combo" ? "✦ Combo" : "⚠ Clash"}</span>${e.found ? `<em>found M${e.found_month}</em>` : ""}</div>
        <b>${e.name ? esc(e.name) : "???"}</b>
        ${e.name ? `<div class="fg-cx-picks">${e.picks.map((p) => `<span>${esc(p)}</span>`).join("")}</div><p>${esc(e.blurb)}</p>
          <div class="fg-cx-eff">${esc(fgEffectLine(e.effect))}</div>` : `<p class="fg-cx-hint">“${esc(e.hint)}”</p>`}
      </div>`).join("")}</div>`, { sub: "Combos stay hidden until a launch uses one; clashes are always shown so they never surprise you." })}`;
  host.querySelectorAll("[data-spin]").forEach((b) => b.addEventListener("click", () => fgOpenVariants(Number(b.dataset.spin))));
  host.querySelectorAll("[data-evolve]").forEach((b) => b.addEventListener("click", () => fgOpenEvolve(nodes[Number(b.dataset.evolve)])));
}

// Late-bound: a later view may replace these (window.X = ...), so callers use window.X.
window.fgDecorate = fgDecorate;
window.fgOpenVariants = fgOpenVariants;
window.fgPrototypeCard = fgPrototypeCard;
window.fgQuickBuild = fgQuickBuild;
window.fgRenderPortfolio = fgRenderPortfolio;
